// Real Apple subscriptions (StoreKit 2) — JS side of the native bridge.
//
// Flow: purchase/entitlement JWS → POST /api/subscription/sync (backend
// verifies Apple's signature and stores the entitlement) → ONLY THEN
// finishTransaction. If the backend sync fails the transaction stays
// unfinished and re-emerges through the native transactionUpdated listener
// on next launch, so entitlement is never lost to a network failure.
import { Capacitor, registerPlugin } from "@capacitor/core";
import api from "@/lib/api";

const Native = registerPlugin("ContinuaSubscription");

export const storeAvailable = () => Capacitor.getPlatform() === "ios";

export async function getStoreProducts() {
  const { products } = await Native.loadProducts();
  return products || [];
}

async function syncSignedTransactions(signedTransactions, signedRenewalInfo) {
  const { data } = await api.post("/subscription/sync", {
    signed_transactions: signedTransactions,
    signed_renewal_info: signedRenewalInfo || null,
  }, { timeout: 25000 });
  return data;
}

async function finishQuietly(transactionId) {
  try { await Native.finishTransaction({ transactionId }); } catch (e) { /* retried via updates */ }
}

// Purchase → verify on backend → finish. Returns:
//   {status:"success", subscription} | {status:"cancelled"} | {status:"pending"}
//   | {status:"sync_failed"} (Apple purchase OK, backend sync failed — will
//   converge via the transaction listener / next launch)
export async function purchaseProduct(productId) {
  const result = await Native.purchase({ productId });
  if (result.status !== "success") return { status: result.status };
  try {
    const subscription = await syncSignedTransactions([result.signedTransactionInfo]);
    await finishQuietly(result.transactionId);
    return { status: "success", subscription };
  } catch (e) {
    console.warn("storekit: purchase sync failed — transaction left unfinished for retry", e?.message);
    return { status: "sync_failed" };
  }
}

// Launch / restore convergence: current verified entitlements → backend.
// An empty list legitimately downgrades an app_store-sourced subscription.
export async function syncEntitlements() {
  const { entitlements } = await Native.currentEntitlements();
  const list = entitlements || [];
  const renewal = list.find((e) => e.signedRenewalInfo)?.signedRenewalInfo || null;
  const subscription = await syncSignedTransactions(list.map((e) => e.signedTransactionInfo), renewal);
  for (const e of list) {
    if (e.transactionId) await finishQuietly(e.transactionId);
  }
  return subscription;
}

export async function restorePurchases() {
  await Native.restore(); // AppStore.sync() — works after reinstall
  return syncEntitlements();
}

let listenerAttached = false;
let launchSyncDone = false;

// Long-lived convergence: renewals, pending completions, restored purchases
// and unfinished transactions from previous launches.
export function initStoreSync() {
  if (!storeAvailable()) return;
  if (!listenerAttached) {
    listenerAttached = true;
    Native.addListener("transactionUpdated", async (event) => {
      if (!event?.signedTransactionInfo) return;
      try {
        await syncSignedTransactions([event.signedTransactionInfo]);
        if (event.transactionId) await finishQuietly(event.transactionId);
      } catch (e) {
        console.warn("storekit: transactionUpdated sync failed — will retry on next launch", e?.message);
      }
    });
  }
  if (!launchSyncDone) {
    launchSyncDone = true;
    syncEntitlements().catch((e) => {
      launchSyncDone = false; // allow a later retry in this session
      console.warn("storekit: launch entitlement sync failed", e?.message);
    });
  }
}
